import { useCallback, useMemo, useState } from 'react';
import type { SearchParamsForResourceType } from '@lifeomic/service-clients/fhir-client';
import { useActiveProject } from '../../../../hooks';
import {
  CODE_ZORI_COACH,
  SYSTEM_ACTION_PLAN,
  SYSTEM_LIFEOMIC_DATASET,
  SYSTEM_ZORI_COACH_SOURCE_RUN,
} from '../../../../util/fhir';
import { useInfiniteFHIRQuery } from '../../../../util/api';

export { CODE_ZORI_COACH, SYSTEM_ZORI_COACH_SOURCE_RUN };

export type ZoriCoachCarePlan = fhir3.CarePlan & {
  id: string;
  meta?: fhir3.Meta;
};

export const isZoriCoachCarePlan = (plan: ZoriCoachCarePlan): boolean =>
  Boolean(
    plan.id &&
      plan.meta?.tag?.some(
        tag =>
          tag.system === SYSTEM_ACTION_PLAN && tag.code === CODE_ZORI_COACH,
      ),
  );

export const getZoriCoachSourceRunId = (
  plan?: ZoriCoachCarePlan,
): string | undefined => {
  const runTags =
    plan?.meta?.tag?.filter(
      tag => tag.system === SYSTEM_ZORI_COACH_SOURCE_RUN && tag.code,
    ) ?? [];
  return runTags.length === 1 ? runTags[0].code : undefined;
};

const getStartTime = (plan: ZoriCoachCarePlan): number | undefined => {
  const time = Date.parse(plan.period?.start ?? '');
  return Number.isNaN(time) ? undefined : time;
};

export const useZoriCoachCarePlans = (subjectId: string) => {
  const [project] = useActiveProject();
  const [historyError, setHistoryError] = useState<unknown>();

  const query = useInfiniteFHIRQuery(
    'GET /v1/fhir/dstu3/CarePlan',
    // date/_sort are valid FHIR CarePlan search params; service-clients'
    // CarePlan SearchParams currently omit them.
    {
      patient: subjectId,
      _tag: [
        `${SYSTEM_LIFEOMIC_DATASET}|${project.id}`,
        `${SYSTEM_ACTION_PLAN}|${CODE_ZORI_COACH}`,
      ],
      date: 'gt1970-01-01',
      _sort: '-date',
    } as SearchParamsForResourceType<'CarePlan'>,
    {
      getNextPageParam: lastPage => {
        const nextUrl = lastPage.link?.find(
          (link: fhir3.BundleLink) => link.relation === 'next',
        )?.url;
        const next = nextUrl
          ? new URL(nextUrl, window.location.origin).searchParams.get('next')
          : null;
        return next ? { next } : undefined;
      },
    },
  );

  const carePlans = useMemo(() => {
    const plans = (query.data?.pages ?? []).flatMap(
      page =>
        page.entry?.map(entry => entry.resource as ZoriCoachCarePlan) ?? [],
    );
    const uniquePlans = Array.from(
      new Map(
        plans
          .filter((plan): plan is ZoriCoachCarePlan => plan != null)
          .filter(isZoriCoachCarePlan)
          .map(plan => [plan.id, plan] as const),
      ).values(),
    );

    return uniquePlans
      .map((plan, sourceIndex) => ({
        plan,
        sourceIndex,
        startTime: getStartTime(plan),
      }))
      .sort((a, b) => {
        if (a.startTime === undefined) {
          return b.startTime === undefined ? a.sourceIndex - b.sourceIndex : 1;
        }
        if (b.startTime === undefined) {
          return -1;
        }
        return b.startTime - a.startTime || a.sourceIndex - b.sourceIndex;
      })
      .map(({ plan }) => plan);
  }, [query.data]);

  const hasPageData = query.data != null;
  const refetchQuery = query.refetch;
  const fetchNextPage = query.fetchNextPage;
  const refetch = useCallback(async () => {
    setHistoryError(undefined);
    return !(await refetchQuery()).isError;
  }, [refetchQuery]);
  const loadOlder = useCallback(async () => {
    setHistoryError(undefined);
    const result = await fetchNextPage();
    if (result.isError) {
      setHistoryError(result.error);
    }
  }, [fetchNextPage]);

  return {
    carePlans,
    latestCarePlan: carePlans[0],
    loading: query.isLoading,
    initialError: hasPageData ? undefined : query.error ?? undefined,
    historyError,
    refetch,
    loadOlder,
    hasOlder: Boolean(query.hasNextPage),
    isLoadingOlder: query.isFetchingNextPage,
  };
};
